import {
  createDefaultColorOptions,
  createMarkupEditorShapeStyleControls,
  createMarkupEditorToolStyles
} from '@pqina/pintura';
import {
  DEFAULT_EDITOR_FONT_FAMILY,
  DEFAULT_EDITOR_FONT_URL
} from '../contants';
import { loadFontFromStylesheet } from '../../../../utils/fonts';

export interface BrandFonts {
  fontFamily?: string;
  fontFaceUrl?: string;
}

export interface TemplateFont {
  family: string;
  faceUrl: string;
}

/**
 * The font an overlay template is written in: whatever the location picked
 * under brand settings, or the editor default when it picked nothing.
 *
 * Everything downstream has to read it from here. The editor used to measure
 * text in the brand font but draw it in the default one, so the text came out
 * at a size that fit a face it was never rendered in.
 */
export const resolveTemplateFont = (fonts?: BrandFonts): TemplateFont => ({
  family: fonts?.fontFamily || DEFAULT_EDITOR_FONT_FAMILY,
  faceUrl: fonts?.fontFaceUrl || DEFAULT_EDITOR_FONT_URL
});

/**
 * Put the template font in the document and wait until it is really usable.
 *
 * Waiting is the point. Pintura lays text out on a canvas and the editor sizes
 * it with `calculateFontSize`, so a measurement taken before the face arrives
 * measures the fallback and is never corrected.
 */
export const loadTemplateFont = async (font: TemplateFont): Promise<boolean> =>
  await loadFontFromStylesheet(font.faceUrl, font.family);

export const TEMPLATE_FONT_SIZE_OPTIONS = [
  12, 16, 24, 32, 48, 64, 72, 96, 105, 115, 128, 144, 192, 256
];

/** How Pintura draws a text box the user adds. */
export const templateTextToolStyles = ({
  family,
  fontSize,
  color
}: {
  family: string;
  fontSize: number;
  // Pintura's own colour shape: normalized RGB(A), not a CSS string.
  color: number[];
}): any =>
  createMarkupEditorToolStyles({
    text: {
      fontSize,
      color: color as any,

      // Enable text background color
      backgroundColor: createDefaultColorOptions().transparent,

      fontFamily: family,
      textAlign: 'left',
      disableNewline: false
    }
  });

/** What Pintura offers in its own text style controls. */
export const templateShapeStyleControls = (family: string): any =>
  createMarkupEditorShapeStyleControls({
    // Set absolute font size values
    fontSizeOptions: TEMPLATE_FONT_SIZE_OPTIONS,
    fontFamilyOptions: [[family, 'Template Font']]
  });
